import { Formik, Form } from "formik";
import { ReactComponent as VisaSvg } from "../../../../assets/payment/visa.svg";
import { ReactComponent as MasterCardSvg } from "../../../../assets/payment/mastercard.svg";
import { ReactComponent as UnionPaySvg } from "../../../../assets/payment/unionpay.svg";
import { ReactComponent as JcbSvg } from "../../../../assets/payment/jcb.svg";
import TextBox from "../../../common/TextBox";
import ComboBox from "../../../common/ComboBox";
import React, { useContext, useEffect, useState } from "react";
import { langComponent, PAGE_STATE } from "../../../../constants";
import AppContext from "../../../../AppContext";
import _ from "lodash";
import { format, startOfMonth } from "date-fns";
import Button from "../../../common/Button";
import { toast } from "react-toastify";
import useUsers from "../../../../hooks/useUsers";
import ProfileContext from "../../ProfileContext";
import axios from "axios";
import useOmise from "../../../../hooks/useOmise";
const omiseSecretKey = import.meta.env.OMISE_SECRET_KEY;

const prefix = "profile-edit-billing-payment";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const Payment = () => {
  const { getLang, user: loggedInUser } = useContext(AppContext);
  const { user, company, isCompany, isOwner } = useContext(ProfileContext);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationProfile = getLang(langComponent.profile);
  const { getPaymentMethod, patchPaymentMethod } = useUsers();
  const { getCard, addCard, deleteCard } = useOmise();
  const [cardId, setCardId] = useState(undefined);
  const [currentCardDetail, setCurrentCardDetail] = useState(undefined);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);

  useEffect(() => {
    if (isCompany && !company) return;
    if (!isCompany && !user) return;

    // * Get current payment method
    init();
  }, [user, company, isCompany]);

  const init = async () => {
    if (isCompany) {
      // TODO: Get company profile
      return;
    }

    const _paymentMethod = await getPaymentMethod(user._id);

    if (!_paymentMethod.data) {
      setPageState(PAGE_STATE.IDLE);
      return;
    }

    if (!_paymentMethod.data) {
      setPageState(PAGE_STATE.IDLE);
      return;
    }

    const { id, last_digits, name, expiration_month, expiration_year } =
      _paymentMethod.data;

    setCardId(id);
    setCurrentCardDetail({
      lastDigits: last_digits,
      card: "",
      cvc: "",
      name: name,
      month: expiration_month.toString(),
      year: expiration_year.toString().slice(2, 4),
    });
    setPageState(PAGE_STATE.IDLE);
  };

  const _getCardType = (cardNumber) => {
    // Remove all non-digit characters from the input
    const sanitized = cardNumber.replace(/\D/g, "");

    // Regex patterns for various card types
    const cardPatterns = {
      visa: /^4\d{12}(\d{3})?$/,
      mastercard:
        /^(5[1-5]\d{4}|222[1-9]|22[3-9]\d{3}|2[3-6]\d{4}|27[01]\d{2}|2720)\d{10}$/,
      jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
      unionpay: /^(62\d{14,17}|6[5-9]\d{13,18})$/,
    };

    // Check the card number against each pattern
    for (const [cardType, pattern] of Object.entries(cardPatterns)) {
      if (pattern.test(sanitized)) {
        return cardType;
      }
    }

    // If no pattern matches, return 'unknown'
    return "unknown";
  };

  const handleValidate = ({ card, month, year, cvc, name }) => {
    let errors = {};

    if (!card) errors.card = TranslationCommon("Required");
    if (!month) errors.month = TranslationCommon("Required");

    if (!year) errors.year = TranslationCommon("Required");
    if (!cvc) errors.cvc = TranslationCommon("Required");
    if (!name) errors.name = TranslationCommon("Required");

    if (year && year.length != 2) errors.year = TranslationCommon("Required");
    if (cvc && cvc.length != 3) errors.cvc = TranslationCommon("Required");

    // * Check expiration date
    if (month && year) {
      const current = startOfMonth(new Date());
      const exp = startOfMonth(new Date(+`20${year}`, +month - 1, 1));

      if (exp < current) errors.year = TranslationCommon("Expired");
    }

    return errors;
  };

  const handleSubmit = (
    { card, month, year, cvc, name },
    { setSubmitting }
  ) => {
    setSubmitting(true);
    Omise.createToken(
      "card",
      {
        expiration_month: +month,
        expiration_year: +`20${year}`,
        number: card,
        security_code: cvc,
        name,
      },
      async (_statusCode, response) => {
        if (!response || !response.card) {
          toast.error("Unable to create card");
          setSubmitting(false);
          console.error(response);
          return;
        }

        const cardToken = response.id;

        try {
          await patchPaymentMethod(loggedInUser._id, cardToken);

          const { data: _newCard } = await getPaymentMethod(loggedInUser._id);

          const { id, last_digits, name, expiration_month, expiration_year } =
            _newCard;

          setCardId(id);
          setCurrentCardDetail({
            lastDigits: last_digits,
            card: "",
            cvc: "",
            name: name,
            month: expiration_month.toString(),
            year: expiration_year.toString().slice(2, 4),
          });

          toast.success("Updated payment method");
          return;
        } catch (error) {
          toast.error("Failed to update payment method");
          console.error("Failed to update card:", result);
        } finally {
          setSubmitting(false);
        }
      }
    );
  };

  if (pageState === PAGE_STATE.LOADING) return <></>;

  return (
    <div className={prefix}>
      <span className="profile-edit-billing__heading">Payment Information</span>
      <Formik
        initialValues={
          currentCardDetail ?? {
            card: "",
            cvc: "",
            name: "",
            year: "",
            month: "",
          }
        }
        enableReinitialize={true}
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        {({ values }) => {
          const _cardType = _getCardType(values.card);

          const getCardTypeClassname = (type) => {
            if (_cardType === type) {
              return "cart-content-payment-form-card-types__icon cart-content-payment-form-card-types__icon--active";
            }

            return "cart-content-payment-form-card-types__icon";
          };

          return (
            <Form className={`${prefix}-form`}>
              <div className={`${prefix}-form-content`}>
                <div className={`${prefix}-form-item`}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 14,
                      color: "#333",
                      margin: "0 8px",
                    }}
                  >
                    Card no
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <VisaSvg
                        className={getCardTypeClassname("visa")}
                        height={20}
                        width={20}
                      />
                      <MasterCardSvg
                        className={getCardTypeClassname("mastercard")}
                        height={20}
                        width={20}
                      />
                      <JcbSvg
                        className={getCardTypeClassname("jcb")}
                        height={20}
                        width={20}
                      />
                      <UnionPaySvg
                        className={getCardTypeClassname("unionpay")}
                        height={20}
                        width={20}
                      />
                    </div>
                  </div>
                  <TextBox
                    name="card"
                    mask="9999 9999 9999 9999"
                    maskPlaceholder=""
                    placeholder={
                      values.lastDigits
                        ? `**** **** **** ${values.lastDigits}`
                        : "0000 0000 0000 0000"
                    }
                  />
                </div>
                <div className={`${prefix}-form-item`}>
                  <div className={`${prefix}-form-carddetail`}>
                    <ComboBox
                      name={"month"}
                      placeholder={"Month"}
                      options={monthOptions}
                      label={"Expiration"}
                    />
                    <TextBox
                      name="year"
                      placeholder={"YY"}
                      label={"Year"}
                      hideLimit
                      mask="99"
                      maskPlaceholder=""
                      autocomplete="off"
                    />
                    <TextBox
                      name="cvc"
                      placeholder={"***"}
                      label={"CVC"}
                      hideLimit
                      mask="999"
                      maskPlaceholder=""
                      autocomplete="off"
                    />
                  </div>
                </div>
                <div className={`${prefix}-form-item`}>
                  <TextBox name="name" label={"Name on card"} />
                </div>
              </div>
              <Button type="submit" label={"Update"} />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

Payment.propTypes = {};

export default Payment;
